import { Location } from "@angular/common";
import { Routes } from "@angular/router";
import {
    LocalizeRouterSettings,
    ManualParserLoader,
} from "@gilsdav/ngx-translate-router";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "../../../environments/environment";
import {
    FeatureFlags,
    hasFeatureFlag,
} from "../../constants/feature-flags.constants";
import { AuthUserGuard } from "../../guards/auth-user.guard";
import { DevModeGuard } from "../../guards/dev-mode.guard";


export function createTranslateLoader(
    translate: TranslateService,
    location: Location,
    settings: LocalizeRouterSettings,
): ManualParserLoader {
    return new ManualParserLoader(
        translate,
        location,
        settings,
        environment.local,
        "ROUTES.",
    );
}


const devRoutes: Routes = !environment.production
    ? [{
        path: "ui-kit",
        loadChildren: () => import("../../modules/ui-kit/ui-kit-routing").then(m => m.uiKitRoutes),
        canLoad: [DevModeGuard],
        data: {
            hideHeader: true,
            hideFooter: true,
            hideArguments: true,
            hideBottomMenu: true,
            hideBottomBanner: true,
        },
    }]
    : [];


const tournamentRoutes: Routes = hasFeatureFlag(FeatureFlags.TOURNAMENTS_URL)
    ? [
        {
            path: "tournaments",
            data: {
                sitemap: {
                    loc: "/tournaments",
                },
            },
            loadChildren: () => import("../../features/tournaments/tournaments-routing").then(m => m.tournamentsRoutes),
        },
        {
            path: "tournaments/:id",
            data: {},
            loadChildren: () => import("../../features/tournaments/tournament-item-routing").then(m => m.tournamentItemRouting),
        },
    ]
    : [];


const achievementsRoutes: Routes = hasFeatureFlag(FeatureFlags.ACHIEVEMENTS_URL)
    ? [
        {
            path: "achievements",
            data: {
                sitemap: {
                    loc: "/achievements",
                },
            },
            loadComponent: () => import("../../features/achievements/achievements.component").then(c => c.AchievementsComponent),
        },
    ]
    : [];


const missionsRoutes: Routes = hasFeatureFlag(FeatureFlags.MISSIONS_URL)
    ? [
        {
            path: "missions",
            data: {
                sitemap: {
                    loc: "/missions",
                },
            },
            loadChildren: () => import("../../features/missions/missions-routing").then(m => m.missionsRoutes),
        },
        {
            path: "missions/:alias",
            data: {},
            loadChildren: () => import("../../features/missions/missions-category-routing").then(m => m.missionsCategoryRouting),
        },
    ]
    : [];


const lotteriesRoutes: Routes = hasFeatureFlag(FeatureFlags.LOTTERIES_URL)
    ? [
        {
            path: "lotteries",
            data: {
                sitemap: {
                    loc: "/lotteries",
                },
            },
            loadChildren: () => import("../../features/lotteries/lotteries-routing").then(m => m.lotteriesRoutes),
        },
    ]
    : [];


const sportRoutes: Routes = hasFeatureFlag(FeatureFlags.SPORT)
    ? [
        {
            path: "sport",
            data: {
                hideHeaderPromo: true,
                hideBottomBanner: true,
                sitemap: {
                    loc: "/sport",
                    priority: "0.8",
                    changefreq: "weekly",
                },
            },
            loadChildren: () => import("../../features/sport/sport-routing").then(m => m.sportRoutes),
        },
    ]
    : [];

export const routes: Routes = [
    {
        path: "",
        data: {
            sitemap: {
                loc: "/",
                priority: "1.0",
            },
        },
        loadChildren: () => import("../../features/home/home-routing").then(m => m.homeRoutes),
    },
    {
        path: "restore",
        loadChildren: () => import("../../features/home/home-routing").then(m => m.homeRoutes),
    },
    {
        path: "game",
        data: {
            hideHeader: true,
            hideFooter: true,
            hideMenu: true,
            hideBottomMenu: true,
            hideBottomBanner: true,
        },
        loadChildren: () => import("../../features/game/game-routing").then(m => m.gameRoutes),
    },
    {
        path: "games",
        data: {
        },
        loadChildren: () => import("../../features/game-hall/game-hall-routing").then(m => m.gameHallRoutes),
    },
    {
        path: "game-categories/:category-name",
        loadChildren: () => import("../../features/game-hall/game-hall-routing").then(m => m.gameHallRoutes),
    },
    {
        path: "game-providers/:provider-name",
        loadChildren: () => import("../../features/game-hall/game-hall-routing").then(m => m.gameHallRoutes),
    },
    {
        path: "shop",
        data: {
            sitemap: {
                loc: "/shop",
                priority: "0.8",
                changefreq: "weekly",
            },
        },
        loadChildren: () => import("../../features/shop/shop-routing").then(m => m.shopRoutes),
    },
    {
        path: "promotions",
        data: {
            sitemap: {
                loc: "/promotions",
                priority: "0.8",
                changefreq: "weekly",
            },
        },
        loadChildren: () => import("../../features/promotions/promo-routing").then(m => m.promoRoutes),
    },
    {
        path: "promotions/:name",
        data: {},
        loadChildren: () => import("../../features/promotions/promo-item-routing").then(m => m.promoItemRouting),
    },
    {
        path: "vip",
        data: {
            sitemap: {
                loc: "/vip",
                priority: "0.8",
                changefreq: "weekly",
            },
        },
        loadChildren: () => import("../../features/vip/vip-routing").then(m => m.vipRoutes),
    },
    ...sportRoutes,
    {
        path: "profile",
        data: {
            sitemap: {
                loc: "/profile",
            },
            isProfilePage: true,
        },
        loadChildren: () => import("../../features/account-profile/account-profile-routing").then(m => m.AccountProfileRoutes),
        canLoad: [AuthUserGuard],
    },
    {
        path: "info",
        data: {
            sitemap: {
                loc: "/info",
            },
        },
        loadChildren: () => import("../../features/info-pages/info-pages-routing").then(m => m.InfoPageRoutes),
    },
    ...tournamentRoutes,
    ...missionsRoutes,
    ...lotteriesRoutes,
    ...achievementsRoutes,
    {
        path: "payment",
        loadChildren: () => import("../../modules/payment/payment-routing").then(m => m.paymentRoutes),
        data: {
            hideHeader: true,
            hideFooter: true,
            hideArguments: true,
            hideBottomMenu: true,
            hideBottomBanner: true,
        },
    },
    ...devRoutes,
    {
        path: "**",
        loadComponent: () => import("../../components/not-found/not-found.component").then(c => c.NotFoundComponent),
        data: { skipRouteLocalization: true },
    },
];
